import {Action, Selector, State, StateContext} from '@ngxs/store';
import {tap} from 'rxjs/operators';
import {FormState} from '../../models/form-state.model';
import {PasswordPolicyService} from '../../services/password-policy.service';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {Injectable} from '@angular/core';
import {PasswordPolicy} from '../../models/password-policy.model';
import {setFormValidationErrors} from '../../helpers/form.helper';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {PasswordPolicyActions} from './password-policy.actions';


interface PasswordPolicyStateModel {
    form: FormState;
    list: SearchList<PasswordPolicy>;
    activePasswordPolicy: PasswordPolicy;
    fieldList: PasswordPolicy[];
}

const STATE_NAME = 'passwordPolicy';

@State<PasswordPolicyStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<PasswordPolicy>({
            filters: new FormState()
        }),
        activePasswordPolicy: null,
        fieldList: null
    }
})

@Injectable()
export class PasswordPolicyState {
    constructor(
        private navService: NavService,
        private passwordPolicyService: PasswordPolicyService
    ) {}

    @Selector()
    static passwordPolicyList(state) {
        return state.list;
    }

    @Selector()
    static activePasswordPolicy(state) {
        return state.activePasswordPolicy;
    }

    @Selector()
    static passwordPolicyFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static passwordPolicyFieldList(state) {
        return state.fieldList;
    }


    @Action(PasswordPolicyActions.GetListPage)
    getListPage(ctx: StateContext<PasswordPolicyStateModel>, action: PasswordPolicyActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.passwordPolicyService.fetchList(props));
    }

    @Action(PasswordPolicyActions.Add)
    addPasswordPolicy(ctx: StateContext<PasswordPolicyStateModel>, {passwordPolicy}: PasswordPolicyActions.Add) {
        ctx.patchState({
            form: new FormState({
                ...ctx.getState().form,
                status: FormStatus.PENDING,
            })
        });
        return this.passwordPolicyService.add(passwordPolicy).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.PASSWORD_POLICY_MANAGEMENT),
            ({error}) => ctx.dispatch(new PasswordPolicyActions.FormFailure(error))
        );
    }

    @Action(PasswordPolicyActions.Update)
    updatePasswordPolicy(ctx: StateContext<PasswordPolicyStateModel>, {passwordPolicy}: PasswordPolicyActions.Update) {
        ctx.patchState({
            form: new FormState({
                ...ctx.getState().form,
                status: FormStatus.PENDING,
            })
        });
        return this.passwordPolicyService.update(passwordPolicy).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.PASSWORD_POLICY_MANAGEMENT),
            ({error}) => ctx.dispatch(new PasswordPolicyActions.FormFailure(error))
        );
    }

    @Action(PasswordPolicyActions.FormFailure)
    addPasswordPolicyFailure(
        ctx: StateContext<PasswordPolicyStateModel>,
        {validationErrors = []}: PasswordPolicyActions.FormFailure
    ) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(PasswordPolicyActions.Delete)
    deletePasswordPolicy(ctx: StateContext<PasswordPolicyStateModel>, {policyId}: PasswordPolicyActions.Delete) {
        return this.passwordPolicyService.delete(policyId).subscribe(
            () => ctx.dispatch(new PasswordPolicyActions.GetListPage())
        );
    }

    @Action(PasswordPolicyActions.GetById)
    getPasswordPolicy(ctx: StateContext<PasswordPolicyStateModel>, {policyId}: PasswordPolicyActions.GetById) {
        return this.passwordPolicyService.fetchById(policyId).pipe(
            tap((passwordPolicy) => {
                ctx.patchState({
                    activePasswordPolicy: new PasswordPolicy(passwordPolicy)
                });
            })
        );
    }

    @Action(PasswordPolicyActions.FieldList)
    getFieldList(ctx: StateContext<PasswordPolicyStateModel>) {
        return this.passwordPolicyService.getList().pipe(
            tap((fieldList: PasswordPolicy[]) => {
                ctx.patchState({fieldList});
            })
        );
    }
}
